import {
  containsRectangle,
  isValidRectangle,
  rectanglesOverlap,
} from 'features/documentMarkup/editor/markupGeometry'
import type { MarkupRectangle } from 'features/documentMarkup/editor/types'
import type {
  MeasuredImage,
  PixelRectanglePolicy,
} from 'features/libra/drawing/pixelRectangleValidation'
import {
  MINIMUM_NATURAL_RECTANGLE_SIZE,
  pixelImageRectangle,
  pixelRectangleFromBounds,
  pixelRectangleMeetsMinimumSize,
  validateMeasuredPixelRectangle,
} from 'features/libra/drawing/pixelRectangleValidation'
import { unwrapFieldValue } from 'features/libra/ontology/ontologyFieldDefaults'
import { FieldValues } from 'features/libra/ontology/ontologyFieldTypes'
import type { ZoneParams } from 'features/libra/stepComponents/zoneAnalysis/zoneAnalysisApi'
import { LibraWorkflowGlobalContext } from 'features/libra/workflow/workflowStepsApi'
import isEqual from 'lodash/isEqual'
import { z } from 'zod'

import { areMethodAssignmentsComplete } from './methodAssignments'
import { getPlanningStrategy } from './planningStrategy'
import {
  InspectionArea,
  InspectionAreaDrawing,
  inspectionAreaSchema,
  InspectionAreaUpdate,
  PlanningAreasConfig,
  planningAreasConfigSchema,
  PlanningStrategies,
  planningStrategiesSchema,
  sourceZonesSchema,
} from './planningTypes'
import { TechniqueCatalogue, techniqueCatalogueSchema } from './techniqueTypes'

const inspectionAreasInputSchema = z.object({
  zones: sourceZonesSchema,
  areas: inspectionAreaSchema.array(),
  strategies: planningStrategiesSchema,
  catalogue: techniqueCatalogueSchema,
})

export function readInspectionAreas(
  rawConfig: Record<string, unknown>,
  fieldValues: FieldValues,
  globalContext: LibraWorkflowGlobalContext
) {
  const config = planningAreasConfigSchema.safeParse(rawConfig)
  if (!config.success) return config
  const input = inspectionAreasInputSchema.safeParse({
    zones: unwrapFieldValue(fieldValues[config.data.sourceZonesField]) ?? [],
    areas: unwrapFieldValue(fieldValues[config.data.areasField]) ?? [],
    strategies: globalContext.inspectionPlanningStrategies,
    catalogue: globalContext[config.data.techniqueCatalogueContextKey],
  })
  if (!input.success) return input
  return {
    success: true as const,
    data: { ...input.data, config: config.data },
  }
}

export function addInspectionArea(
  areas: readonly InspectionArea[],
  zone: ZoneParams,
  id: string,
  drawing?: InspectionAreaDrawing
): InspectionArea[] {
  return [
    ...areas,
    {
      id,
      sourceZoneId: zone.id,
      title: `${zone.title} · ${drawing ? `Area ${areas.filter((area) => area.sourceZoneId === zone.id).length + 1}` : 'Whole zone'}`,
      ...(drawing ?? { scope: 'zone' as const }),
      methodAssignments: [],
    },
  ]
}

const rectangleFor = (
  bounds: InspectionArea['drawing_bounds']
): MarkupRectangle | undefined => bounds && pixelRectangleFromBounds(bounds)

type InspectionAreaPixelPolicy = PixelRectanglePolicy & {
  boundary: MarkupRectangle
}

export type InspectionAreaSourceBox = Readonly<{
  index: number
  boundary: Readonly<MarkupRectangle>
}>

type InspectionAreaSourceGeometry = Readonly<{
  imageId?: string
  boxes: readonly InspectionAreaSourceBox[]
  error?: 'missing-image' | 'invalid-boundary' | 'outside-image'
}>

type InspectionAreaGeometry = Pick<
  InspectionArea,
  'id' | 'sourceZoneId' | 'scope' | 'zone_reference_image_id' | 'drawing_bounds'
>

type InspectionAreaGeometryAnalysis = {
  measuredImage?: MeasuredImage
  sourcesById: ReadonlyMap<string, InspectionAreaSourceGeometry>
  obstaclesByImage: ReadonlyMap<
    string,
    readonly PixelRectanglePolicy['obstacles'][number][]
  >
  malformedAreasByImage: ReadonlyMap<
    string,
    ReadonlyMap<string, 'missing-image' | 'invalid-rectangle'>
  >
  areasById: ReadonlyMap<string, InspectionAreaGeometry>
}

type InspectionAreaGeometryLabels = {
  areaTitlesById: ReadonlyMap<string, string>
  sourceTitlesById: ReadonlyMap<string, string>
}

export const analyzeInspectionAreaGeometry = ({
  areas,
  zones,
  measuredImage,
}: {
  areas: readonly InspectionArea[]
  zones: readonly ZoneParams[]
  measuredImage?: MeasuredImage
}): InspectionAreaGeometryAnalysis => {
  const sourcesById = new Map<string, InspectionAreaSourceGeometry>()
  for (const zone of zones) {
    if (sourcesById.has(zone.id)) continue
    const imageId = zone.zone_reference_image_id
    const validBoxes = (zone.drawing_bounds ?? []).flatMap((bounds, index) => {
      const boundary = pixelRectangleFromBounds(bounds)
      return boundary &&
        isValidRectangle(boundary) &&
        pixelRectangleMeetsMinimumSize(boundary)
        ? [{ index, boundary }]
        : []
    })
    const imageBounds =
      imageId && measuredImage?.documentId === imageId
        ? pixelImageRectangle(measuredImage)
        : undefined
    const boxes = imageBounds
      ? validBoxes.filter(({ boundary }) =>
          containsRectangle(imageBounds, boundary)
        )
      : validBoxes
    const error = !imageId
      ? 'missing-image'
      : boxes.length > 0
        ? undefined
        : validBoxes.length > 0 && imageBounds
          ? 'outside-image'
          : 'invalid-boundary'
    sourcesById.set(zone.id, {
      imageId,
      boxes,
      error,
    })
  }

  const obstaclesByImage = new Map<
    string,
    PixelRectanglePolicy['obstacles'][number][]
  >()
  const malformedAreasByImage = new Map<
    string,
    Map<string, 'missing-image' | 'invalid-rectangle'>
  >()
  const areasById = new Map<string, InspectionAreaGeometry>()
  for (const area of areas) {
    areasById.set(area.id, {
      id: area.id,
      sourceZoneId: area.sourceZoneId,
      scope: area.scope,
      zone_reference_image_id: area.zone_reference_image_id,
      drawing_bounds: area.drawing_bounds && { ...area.drawing_bounds },
    })
    if (area.scope === 'zone') continue
    const imageId =
      area.zone_reference_image_id ||
      sourcesById.get(area.sourceZoneId)?.imageId
    if (!imageId) continue
    const rectangle = rectangleFor(area.drawing_bounds)
    if (
      !area.zone_reference_image_id ||
      !rectangle ||
      !isValidRectangle(rectangle)
    ) {
      const malformedAreas = malformedAreasByImage.get(imageId) ?? new Map()
      malformedAreas.set(
        area.id,
        area.zone_reference_image_id ? 'invalid-rectangle' : 'missing-image'
      )
      malformedAreasByImage.set(imageId, malformedAreas)
    } else {
      const obstacles = obstaclesByImage.get(imageId) ?? []
      obstacles.push({ id: area.id, rectangle })
      obstaclesByImage.set(imageId, obstacles)
    }
  }

  return {
    measuredImage: measuredImage && { ...measuredImage },
    sourcesById,
    obstaclesByImage,
    malformedAreasByImage,
    areasById,
  }
}

export const findInspectionAreaSourceBoxIndex = (
  geometry: InspectionAreaGeometryAnalysis,
  sourceZoneId: string,
  drawingBounds: InspectionArea['drawing_bounds']
): number | undefined => {
  const rectangle = rectangleFor(drawingBounds)
  if (!rectangle || !isValidRectangle(rectangle)) return undefined
  return geometry.sourcesById
    .get(sourceZoneId)
    ?.boxes.find(({ boundary }) => containsRectangle(boundary, rectangle))
    ?.index
}

export const canRedrawInspectionArea = (
  geometry: InspectionAreaGeometryAnalysis,
  sourceZoneId: string,
  areaId: string
) => {
  const source = geometry.sourcesById.get(sourceZoneId)
  const malformedAreas = source?.imageId
    ? geometry.malformedAreasByImage.get(source.imageId)
    : undefined
  return Boolean(
    source?.imageId &&
    !source.error &&
    (!malformedAreas?.size ||
      (malformedAreas.size === 1 && malformedAreas.has(areaId)))
  )
}

export const buildInspectionAreaPixelPolicy = ({
  sourceZoneId,
  geometry,
  labels,
  areaTitle,
  excludeAreaId,
  sourceBoxIndex,
}: {
  sourceZoneId: string
  geometry: InspectionAreaGeometryAnalysis
  labels: InspectionAreaGeometryLabels
  areaTitle?: string
  excludeAreaId?: string
  sourceBoxIndex?: number
}): InspectionAreaPixelPolicy | { error: string } => {
  const source = geometry.sourcesById.get(sourceZoneId)
  if (!source)
    return {
      error: areaTitle
        ? `${areaTitle} cannot be edited because its source zone is unavailable.`
        : 'The selected source zone is unavailable. New areas and redraw are unavailable.',
    }
  if (!source.imageId) {
    const sourceTitle = labels.sourceTitlesById.get(sourceZoneId)
    return {
      error: `${sourceTitle} has no reference image. New areas and redraw are unavailable.`,
    }
  }
  if (source.error === 'outside-image') {
    const sourceTitle = labels.sourceTitlesById.get(sourceZoneId)
    return {
      error: areaTitle
        ? `${areaTitle} cannot be edited because ${sourceTitle} extends outside its reference image.`
        : `${sourceTitle} extends outside its reference image. New areas and redraw are unavailable.`,
    }
  }
  if (source.error === 'invalid-boundary' || !source.boxes.length) {
    const sourceTitle = labels.sourceTitlesById.get(sourceZoneId)
    return {
      error: areaTitle
        ? `${areaTitle} cannot be edited because its source zone has no valid rectangle.`
        : `${sourceTitle} has no valid source rectangle. New areas and redraw are unavailable.`,
    }
  }

  const sourceBox =
    sourceBoxIndex === undefined && source.boxes.length === 1
      ? source.boxes[0]
      : source.boxes.find((box) => box.index === sourceBoxIndex)
  if (!sourceBox) {
    const sourceTitle = labels.sourceTitlesById.get(sourceZoneId)
    return {
      error: areaTitle
        ? `${areaTitle} needs a source rectangle choice before it can be edited.`
        : `${sourceTitle} has multiple source rectangles. Choose one before drawing an inspection area.`,
    }
  }

  const malformedAreas = geometry.malformedAreasByImage.get(source.imageId)
  const malformedPeer =
    malformedAreas && [...malformedAreas].find(([id]) => id !== excludeAreaId)
  if (malformedPeer) {
    const [peerId, issue] = malformedPeer
    const peerTitle = labels.areaTitlesById.get(peerId) ?? peerId
    const reason =
      issue === 'invalid-rectangle'
        ? 'has no valid rectangle on this image.'
        : 'has no reference image on this image.'
    return {
      error: areaTitle
        ? `${areaTitle} cannot be edited because ${peerTitle} ${reason}`
        : `${peerTitle} has missing or invalid image geometry on this image. Redraw or delete it before adding geometry.`,
    }
  }
  return {
    documentId: source.imageId,
    boundary: sourceBox.boundary,
    obstacles: (geometry.obstaclesByImage.get(source.imageId) ?? []).filter(
      ({ id }) => id !== excludeAreaId
    ),
  }
}

export const inspectionAreaGeometryChanged = (
  previous: InspectionAreaGeometry,
  next: InspectionAreaGeometry
) =>
  (previous.scope === 'zone') !== (next.scope === 'zone') ||
  previous.zone_reference_image_id !== next.zone_reference_image_id ||
  !isEqual(previous.drawing_bounds, next.drawing_bounds)

const measuredGeometryError = (
  areaTitle: string,
  error: ReturnType<typeof validateMeasuredPixelRectangle>
) => {
  if (error === 'minimum-size')
    return `${areaTitle} must be at least ${MINIMUM_NATURAL_RECTANGLE_SIZE} pixels wide and high.`
  if (error === 'outside-image')
    return `${areaTitle} must stay inside its reference image.`
  if (error === 'invalid-image-size')
    return `${areaTitle} cannot be edited because its image dimensions are invalid.`
  if (error === 'invalid-rectangle')
    return `${areaTitle} has an invalid rectangle. Redraw it before saving.`
}

const validateAreaGeometry = ({
  area,
  previous,
  requiredMeasurement,
  geometry,
  labels,
}: {
  area: InspectionAreaGeometry
  previous?: InspectionAreaGeometry
  requiredMeasurement: boolean
  geometry: InspectionAreaGeometryAnalysis
  labels: InspectionAreaGeometryLabels
}): string | undefined => {
  const areaTitle = labels.areaTitlesById.get(area.id) ?? area.id
  if (previous && previous.sourceZoneId !== area.sourceZoneId)
    return `${areaTitle} cannot change its source zone.`
  if (
    area.scope === 'zone' ||
    (previous &&
      !inspectionAreaGeometryChanged(previous, area) &&
      !requiredMeasurement)
  )
    return

  const rectangle = rectangleFor(area.drawing_bounds)
  if (!rectangle)
    return `${areaTitle} needs a rectangle before it can be saved.`
  if (!isValidRectangle(rectangle))
    return `${areaTitle} has an invalid rectangle. Redraw it before saving.`
  if (!area.zone_reference_image_id)
    return `${areaTitle} needs a reference image before its rectangle can be saved.`

  const source = geometry.sourcesById.get(area.sourceZoneId)
  if (!source)
    return `${areaTitle} cannot be edited because its source zone is unavailable.`
  if (!source.imageId || source.imageId !== area.zone_reference_image_id)
    return `${areaTitle} must use its source zone image.`
  if (source.error) {
    const sourcePolicy = buildInspectionAreaPixelPolicy({
      sourceZoneId: area.sourceZoneId,
      geometry,
      labels,
      areaTitle,
      excludeAreaId: area.id,
    })
    if ('error' in sourcePolicy) return sourcePolicy.error
  }

  const sourceBoxIndex = findInspectionAreaSourceBoxIndex(
    geometry,
    area.sourceZoneId,
    area.drawing_bounds
  )
  if (sourceBoxIndex === undefined) {
    const sourceTitle = labels.sourceTitlesById.get(area.sourceZoneId)
    return `${areaTitle} must stay inside one rectangle in ${sourceTitle}.`
  }

  const policy = buildInspectionAreaPixelPolicy({
    sourceZoneId: area.sourceZoneId,
    geometry,
    labels,
    areaTitle,
    excludeAreaId: area.id,
    sourceBoxIndex,
  })
  if ('error' in policy) return policy.error
  if (!containsRectangle(policy.boundary, rectangle)) {
    const sourceTitle = labels.sourceTitlesById.get(area.sourceZoneId)
    return `${areaTitle} must stay inside ${sourceTitle}.`
  }

  if (
    requiredMeasurement &&
    geometry.measuredImage?.documentId !== policy.documentId
  ) {
    const sourceTitle = labels.sourceTitlesById.get(area.sourceZoneId)
    return `Open ${sourceTitle}'s reference image to edit ${areaTitle}.`
  }

  if (geometry.measuredImage?.documentId === policy.documentId) {
    const geometryError = measuredGeometryError(
      areaTitle,
      validateMeasuredPixelRectangle(rectangle, geometry.measuredImage)
    )
    if (geometryError) return geometryError
  }

  for (const obstacle of policy.obstacles) {
    if (!rectanglesOverlap(rectangle, obstacle.rectangle)) continue
    const peerTitle = labels.areaTitlesById.get(obstacle.id) ?? obstacle.id
    return `${areaTitle} overlaps ${peerTitle}. Move or redraw the rectangle before saving.`
  }
}

export const inspectionAreaGeometryLabels = (
  areas: readonly Pick<InspectionArea, 'id' | 'title'>[],
  zones: readonly Pick<ZoneParams, 'id' | 'title'>[]
): InspectionAreaGeometryLabels => {
  const areaTitlesById = new Map<string, string>()
  for (const { id, title } of areas)
    if (!areaTitlesById.has(id)) areaTitlesById.set(id, title)
  const sourceTitlesById = new Map<string, string>()
  for (const { id, title } of zones)
    if (!sourceTitlesById.has(id)) sourceTitlesById.set(id, title)
  return { areaTitlesById, sourceTitlesById }
}

export const getInspectionAreaGeometryError = ({
  geometry,
  areaId,
  labels,
}: {
  geometry: InspectionAreaGeometryAnalysis
  areaId: string
  labels: InspectionAreaGeometryLabels
}): string | undefined => {
  const area = geometry.areasById.get(areaId)
  if (!area) return
  return validateAreaGeometry({
    area,
    requiredMeasurement: area.scope !== 'zone',
    geometry,
    labels,
  })
}

export const validateInspectionAreaGeometryChanges = ({
  baselineAreas,
  proposedAreas,
  zones,
  measuredImage,
  requireMeasuredImageIds = [],
}: {
  baselineAreas: readonly InspectionArea[]
  proposedAreas: readonly InspectionArea[]
  zones: readonly ZoneParams[]
  measuredImage?: MeasuredImage
  requireMeasuredImageIds?: readonly string[]
}): string | undefined => {
  const geometry = analyzeInspectionAreaGeometry({
    areas: proposedAreas,
    zones,
    measuredImage,
  })
  const labels = inspectionAreaGeometryLabels(proposedAreas, zones)
  const baselineById = new Map(baselineAreas.map((area) => [area.id, area]))
  const requiredMeasurements = new Set(requireMeasuredImageIds)

  for (const area of proposedAreas) {
    const error = validateAreaGeometry({
      area,
      previous: baselineById.get(area.id),
      requiredMeasurement: requiredMeasurements.has(area.id),
      geometry,
      labels,
    })
    if (error) return error
  }
}

export const updateInspectionArea = (
  areas: readonly InspectionArea[],
  id: string,
  update: InspectionAreaUpdate
) => areas.map((area) => (area.id === id ? { ...area, ...update } : area))

export const removeInspectionArea = (
  areas: readonly InspectionArea[],
  id: string
) => areas.filter((area) => area.id !== id)

export function areInspectionAreasComplete(
  zones: readonly ZoneParams[],
  areas: readonly InspectionArea[],
  config: PlanningAreasConfig,
  strategies: PlanningStrategies,
  catalogue: TechniqueCatalogue
): boolean {
  return (
    zones.length > 0 &&
    zones.every((zone) => {
      const strategyConfig = getPlanningStrategy(zone, strategies)
      return (
        strategyConfig.strategyType &&
        strategyConfig.plannedCoveragePercent !== undefined &&
        (!strategyConfig.detailedDrawing ||
          areas.some(
            (area) =>
              area.sourceZoneId === zone.id &&
              area.scope !== 'zone' &&
              area.drawing_bounds &&
              area.zone_reference_image_id
          )) &&
        (!strategyConfig.requirePlan ||
          areas.some((area) => area.sourceZoneId === zone.id))
      )
    }) &&
    areas.every(
      (area) =>
        zones.some((zone) => zone.id === area.sourceZoneId) &&
        !!area.title.trim() &&
        (area.scope === 'zone' ||
          (!!area.drawing_bounds && !!area.zone_reference_image_id)) &&
        areMethodAssignmentsComplete(area, catalogue, config.methodPlanning)
    )
  )
}
